import React, { useMemo } from 'react';
import { v4 } from 'uuid';

import { FormPage } from 'src/legacy/components/Page/FormPage';
import { BillingSettings } from 'src/routes/billing/payment-links/type';
import { useProductStoreForm } from 'src/routes/billing/store/hooks/useProductStoreForm';
import { useProductStoreFormHeader } from 'src/routes/billing/store/hooks/useProductStoreFormHeader';
import {
  ProductStoreSchema,
  ProductStoreVisibility,
} from 'src/routes/billing/store/schema';
import {
  Currencies,
  LineItemType,
  PaymentMethodType,
} from 'src/services/api/invoicesApi';
import { Price, Product } from 'src/services/api/productsApi';
import { ProductStore } from 'src/services/api/productStoreApi';
import { arrayToMap } from 'src/utils/array';

export interface ProductStoreEditorProps {
  onSubmit: (data: ProductStoreSchema, isPublish: boolean) => Promise<void>;
  productStores: ProductStore[];
  defaultCurrency: string;
  billingSettings: BillingSettings | undefined;
  portalPrices: Price[];
  products: Product[];
  isProductStoreLoading: boolean;
  onCopyDomainUrl: () => void;
  basePortalDomain: string;
}

export const ProductStoreForm = ({
  productStores,
  defaultCurrency,
  onSubmit,
  billingSettings,
  portalPrices,
  products,
  isProductStoreLoading,
  onCopyDomainUrl,
  basePortalDomain,
}: ProductStoreEditorProps) => {
  const DEFAULT_PREFERENCES = {
    paymentMethodTypes: [PaymentMethodType.CreditCard],
    absorbTransactionFee: [],
  };
  const [shareProductStoreModalOpen, setShareProductStoreModalOpen] =
    React.useState<boolean>(false);

  const isDefaultCurrencyUSD = defaultCurrency.toUpperCase() == Currencies.USD;
  const productStore = productStores.at(0);

  const portalPricesMap = useMemo(
    () => arrayToMap(portalPrices, 'ref'),
    [portalPrices],
  );

  const paymentMethodTypes: PaymentMethodType[] = [
    billingSettings?.allowPaymentViaCC && PaymentMethodType.CreditCard,
    // For NON-USD currencies, we do not allow ACH payments
    billingSettings?.allowPaymentViaACH &&
      isDefaultCurrencyUSD &&
      PaymentMethodType.BankAccount,
  ].filter(Boolean);

  const absorbTransactionFee: PaymentMethodType[] = [
    billingSettings?.absorbCreditCardFee && PaymentMethodType.CreditCard,

    // For NON-USD currencies, since we do not allow ACH payments, we do not absorb ACH fees
    billingSettings?.absorbACHFees &&
      isDefaultCurrencyUSD &&
      PaymentMethodType.BankAccount,
  ].filter(Boolean);

  const paymentPreferences = {
    paymentMethodTypes: paymentMethodTypes,
    absorbTransactionFee: absorbTransactionFee,
  };

  const initialFormValue = useMemo(() => {
    let item: ProductStoreSchema = {
      id: '',
      displayName: '',
      productStorePreferences: {
        visibility: ProductStoreVisibility.Internal,
        showOnlyOneTimeProducts: true,
        showOnlySubscriptions: true,
        paymentPreferences: billingSettings
          ? paymentPreferences
          : DEFAULT_PREFERENCES,
        currency: defaultCurrency,
      },
      subscriptions: [
        {
          name: '',
          productId: '',
          type: LineItemType.Product,
          priceId: '',
          id: v4(),
          quantity: '1',
          amount: '',
        },
      ],
      oneTimeProducts: [
        {
          name: '',
          productId: '',
          type: LineItemType.Product,
          priceId: '',
          id: v4(),
          quantity: '1',
          amount: '',
        },
      ],
    };
    if (productStore) {
      item = {
        ...item,
        id: productStore.id || '',
        displayName: productStore.displayName || '',
        productStorePreferences: {
          ...item.productStorePreferences,
          visibility: productStore.productStorePreferences.visibility,
          showOnlyOneTimeProducts:
            productStore.productStorePreferences.showOnlyOneTimeProducts,
          showOnlySubscriptions:
            productStore.productStorePreferences.showOnlySubscriptions,
          paymentPreferences: {
            ...item.productStorePreferences.paymentPreferences,
            paymentMethodTypes:
              productStore.productStorePreferences.paymentPreferences
                .paymentMethodTypes || [],
            absorbTransactionFee:
              productStore.productStorePreferences.paymentPreferences
                .absorbTransactionFee || [],
          },
          currency: defaultCurrency,
        },
        oneTimeProducts:
          productStore.oneTimeProducts.length > 0
            ? productStore.oneTimeProducts.map((li) => ({
                ...li,
                amount: `${
                  (Number(portalPricesMap.get(li.priceId ?? '')?.amount) || 0) /
                  100
                }`,
                id: v4(),
                type: LineItemType.Product,
                name: products.find((p) => p.id === li.productId)?.name || '',
                description: li.description || '',
                productId: li.productId || '',
                priceId: li.priceId || '',
                features: li.features || [],
                isEditing: false,
                isValid: true,
              }))
            : [
                {
                  description: '',
                  name: '',
                  productId: '',
                  type: LineItemType.Product,
                  priceId: '',
                  id: v4(),
                  quantity: '1',
                  amount: '',
                },
              ],
        subscriptions:
          productStore.subscriptions.length > 0
            ? productStore.subscriptions.map((li) => ({
                ...li,
                amount: `${
                  (Number(portalPricesMap.get(li.priceId ?? '')?.amount) || 0) /
                  100
                }`,
                id: v4(),
                type: LineItemType.Product,
                name:
                  li.name ||
                  products.find((p) => p.id === li.productId)?.name ||
                  '',
                description: li.description || '',
                productId: li.productId || '',
                priceId: li.priceId || '',
                features: li.features || [],
                isEditing: false,
                isValid: true,
              }))
            : [
                {
                  description: '',
                  name: '',
                  productId: '',
                  type: LineItemType.Product,
                  priceId: '',
                  id: v4(),
                  quantity: '1',
                  amount: '',
                },
              ],
      };
    }

    return item;
  }, [productStore, billingSettings, portalPricesMap, defaultCurrency]);

  const handleProductStore = async (
    data: ProductStoreSchema,
    isPublish: boolean,
  ) => {
    if (productStore) {
      const payload = {
        ...data,
        id: productStore.id || '',
      };
      await onSubmit(payload, isPublish);
    } else {
      await onSubmit(data, isPublish);
    }
  };

  const ProductStoreFormHeader = useProductStoreFormHeader({
    onClick: handleProductStore,
    shareProductStoreModalOpen,
    onShareProductStoreModal: setShareProductStoreModalOpen,
    isProductStoreLoading,
    onCopyDomainUrl,
    basePortalDomain,
  });
  return (
    <div className="h-full px-4 md:px-0 md:pl-3 ">
      <FormPage
        HeaderComponent={ProductStoreFormHeader}
        initialFormValue={initialFormValue}
        useFormHook={useProductStoreForm}
        handleSave={async () => setShareProductStoreModalOpen(true)}
        resetFormOnCancel={false}
        shouldShowPageAction
        lg={8}
        xs={12}
        headerVariant="bottomBorder"
        formStyle={{
          height: '100%',
        }}
      />
    </div>
  );
};
